<div class="wrapper">
    <section class="container">

        <div class="row features justify-content-center">
            <section class="col-12 col-12-narrower feature text-start">

                <header class="major">
                    <h2 i18n>Lombok vs. Java Records vs. Native Implementierung: Ein Vergleich</h2>
                </header>

                <section>
                    <h3 i18n>Einführung</h3>
                    <p i18n>
                        In diesem Beitrag vergleichen wir Lombok, Java Records und die native Implementierung in
                        Java, um Ihnen zu helfen, die beste Wahl für Ihre Programmieraufgaben zu treffen. Jeder
                        Ansatz hat seine eigenen Stärken und Schwächen, die wir im Detail betrachten werden.
                    </p>
                </section>
                <section>
                    <h3 i18n>Lombok</h3>
                    <p i18n>
                        Lombok ist eine Bibliothek, die darauf abzielt, den Boilerplate-Code in Java-Anwendungen zu
                        reduzieren. Mit Lombok können Sie einfache Annotationen verwenden, um automatisch Getter,
                        Setter, <code class="language-java">equals()</code>, <code class="language-java">hashCode()</code> und <code class="language-java">toString()</code>
                        Methoden
                        zu generieren.
                    </p>
                    <h3 i18n>Weitere Details über Lombok und die zugehörige Implementierung</h3>
                    <p i18n>Im Folgenden finden Sie ein Beispiel für eine Implementierung eines DTO (DataTransferObject)
                        mit Builder-Pattern in Lobmok:</p>
                    <pre>
                    <code class="language-java">
import com.fasterxml.jackson.annotation.JsonInclude;

import jakarta.annotation.Nullable;
import lombok.Builder;
import lombok.Data;
import lombok.EqualsAndHashCode;
import lombok.ToString;

&#64;JsonInclude(JsonInclude.Include.NON_EMPTY)
&#64;Data
&#64;EqualsAndHashCode
&#64;ToString
&#64;Builder
public class LombokCustomerDTO {{'{'}}

    //Dlombok

    &#64;Nullable
    private Long id;
    private String firstName;
    private String lastName;
    private String street;
    private String zip;
    private String city;
    &#64;Nullable
    private String phone;
    &#64;Nullable
    &#64;EqualsAndHashCode.Exclude
    &#64;ToString.Exclude
    private String mail;
{{'}'}}
                    </code>
                </pre>
                    <h3 i18n>Lombok-Annotationen</h3>
                    <table border="1" cellpadding="10">
                        <thead>
                            <tr class="tableHeader">
                                <th i18n>Annotation</th>
                                <th i18n>Beschreibung</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="row1">
                                <td><code class="language-java">&#64;Data</code></td>
                                <td i18n>Generiert Getter, Setter, <code class="language-java">toString()</code>, <code class="language-java">equals()</code> und
                                    <code class="language-java">hashCode()</code> Methoden für alle Felder der Klasse.
                                </td>
                            </tr>
                            <tr class="row0">
                                <td><code class="language-java">&#64;Getter / &#64;Setter</code></td>
                                <td i18n>Generiert Getter- und Setter-Methoden für die angegebenen Felder oder die ganze
                                    Klasse.</td>
                            </tr>
                            <tr class="row1">
                                <td><code class="language-java">&#64;NoArgsConstructor</code></td>
                                <td i18n>Generiert einen standardmäßigen No-Arg-Konstruktor.</td>
                            </tr>
                            <tr class="row0">
                                <td><code class="language-java">&#64;AllArgsConstructor</code></td>
                                <td i18n>Generiert einen All-Arg-Konstruktor (Konstruktor mit allen Feldern als
                                    Argumente).</td>
                            </tr>
                            <tr class="row1">
                                <td><code class="language-java">&#64;RequiredArgsConstructor</code></td>
                                <td i18n>Generiert einen Konstruktor für alle finalen Felder und für Felder, die mit
                                    <code class="language-java">&#64;NonNull</code> markiert sind.
                                </td>
                            </tr>
                            <tr class="row0">
                                <td><code class="language-java">&#64;Builder</code></td>
                                <td i18n>Implementiert das Builder-Pattern für die Klasse.</td>
                            </tr>
                            <tr class="row1">
                                <td><code class="language-java">&#64;SneakyThrows</code></td>
                                <td i18n>Wird verwendet, um geprüfte Ausnahmen ohne Deklaration in der Methode oder
                                    Verwendung von try-catch zu werfen.</td>
                            </tr>
                            <tr class="row0">
                                <td><code class="language-java">&#64;Slf4j</code></td>
                                <td i18n>Fügt der Klasse ein SLF4J-Logger-Feld hinzu.</td>
                            </tr>
                            <tr class="row1">
                                <td><code class="language-java">&#64;NonNull</code></td>
                                <td i18n>Wird verwendet, um zu markieren, dass ein Feld nicht null sein darf; generiert
                                    entsprechende Überprüfungen in generierten Methoden.</td>
                            </tr>
                            <tr class="row0">
                                <td><code class="language-java">&#64;Value</code></td>
                                <td i18n>Macht die Klasse unveränderlich (alle Felder final) und generiert entsprechende
                                    Methoden wie bei <code class="language-java">&#64;Data</code>.</td>
                            </tr>
                        </tbody>
                    </table>
                </section>
                <section>
                    <h3 i18n>Java Records</h3>
                    <p i18n>
                        Java Records, eingeführt in Java 14, sind spezielle Klassen, die unveränderliche
                        Datenstrukturen darstellen. Sie bieten eine vereinfachte Syntax und generieren automatisch
                        Methoden wie <code class="language-java">equals()</code> und <code class="language-java">hashCode()</code>.
                    </p>
                    <h3 i18n>Weitere Details über Java Records und ihre Implementierung</h3>
                    <p i18n>Im Folgenden finden Sie ein Beispiel für eine Java Records-Implementierung eines DTO
                        (DataTransferObject) mit Builder-Pattern:</p>
                    <pre>
                    <code class="language-java">
import static com.google.common.base.Preconditions.checkNotNull;
import com.fasterxml.jackson.annotation.JsonInclude;
import jakarta.annotation.Nullable;

&#64;JsonInclude(JsonInclude.Include.NON_EMPTY)
public record RecordCustomerDTO(Long id, String firstName, String lastName, String street, String zip, String city,
    String phone, String mail) {{'{'}}

    public RecordCustomerDTO(final Long id, final String firstName, final String lastName, final String street,
            final String zip, final String city, final String phone, final String mail) {{'{'}}
        this.id = id;
        this.firstName = checkNotNull(firstName, "missing firstName");
        this.lastName = checkNotNull(lastName, "missing lastName");
        this.street = checkNotNull(street, "missing street");
        this.zip = checkNotNull(zip, "missing zip");
        this.city = checkNotNull(city, "missing city");
        this.phone = phone;
        this.mail = mail;
    {{'}'}}

    public static Builder builder() {{'{'}}
        return new Builder();
    {{'}'}}

    public static class Builder {{'{'}}

        &#64;Nullable
        private Long id;
        private String firstName;
        private String lastName;
        private String street;
        private String zip;
        private String city;
        &#64;Nullable
        private String phone;
        &#64;Nullable
        private String mail;

        public RecordCustomerDTO build() {{'{'}}
            return new RecordCustomerDTO(this.id, this.firstName, this.lastName, this.street, this.zip, this.city,
                    this.phone, this.mail);
        {{'}'}}

        public Builder city(final String city) {{'{'}}
            this.city = city;
            return this;
        {{'}'}}

        public Builder firstName(final String firstName) {{'{'}}
            this.firstName = firstName;
            return this;
        {{'}'}}

        public Builder id(&#64;Nullable final Long id) {{'{'}}
            this.id = id;
            return this;
        {{'}'}}

        public Builder lastName(final String lastName) {{'{'}}
            this.lastName = lastName;
            return this;
        {{'}'}}

        public Builder mail(&#64;Nullable final String mail) {{'{'}}
            this.mail = mail;
            return this;
        {{'}'}}

        public Builder phone(&#64;Nullable final String phone) {{'{'}}
            this.phone = phone;
            return this;
            {{'}'}}

        public Builder street(final String street) {{'{'}}
            this.street = street;
            return this;
         {{'}'}}

        public Builder zip(final String zip) {{'{'}}
            this.zip = zip;
            return this;
        {{'}'}}
    {{'}'}}
{{'}'}}
                    </code>
                </pre>
                </section>
                <section>
                    <h3 i18n>Native Implementierung</h3>
                    <p i18n>
                        Die native Implementierung in Java erfordert, dass Sie alle Methoden manuell implementieren.
                        Dies gibt Ihnen volle Kontrolle, bedeutet aber auch mehr Schreibaufwand und erhöhte
                        Fehleranfälligkeit.
                    </p>
                    <h3 i18n>Weitere Details über Native Implementierung</h3>
                    <p i18n>Im Folgenden finden Sie ein Beispiel für eine native Java-Implementierung eines DTO
                        (DataTransferObject) mit Builder-Pattern:</p>
                    <pre>
                    <code class="language-java">
import static com.google.common.base.Preconditions.checkNotNull;
import java.util.Objects;
import java.util.Optional;
import com.fasterxml.jackson.annotation.JsonInclude;
import com.google.common.base.MoreObjects;
import jakarta.annotation.Nullable;

&#64;JsonInclude(JsonInclude.Include.NON_EMPTY)
public class NativeCustomerDTO {{'{'}}

    public static Builder builder() {{'{'}}
        return new Builder();
    {{'}'}}

    public static class Builder {{'{'}}

        &#64;Nullable
        private Long id;
        private String firstName;
        private String lastName;
        private String street;
        private String zip;
        private String city;
        &#64;Nullable
        private String phone;
        &#64;Nullable
        private String mail;

        public Builder city(final String city) {{'{'}}
            this.city = city;
            return this;
        {{'}'}}

        public Builder firstName(final String firstName) {{'{'}}
            this.firstName = firstName;
            return this;
        {{'}'}}

        public Builder id(&#64;Nullable final Long id) {{'{'}}
            this.id = id;
            return this;
        {{'}'}}

        public Builder lastName(final String lastName) {{'{'}}
            this.lastName = lastName;
            return this;
        {{'}'}}

        public Builder mail(&#64;Nullable final String mail) {{'{'}}
            this.mail = mail;
            return this;
        {{'}'}}

        public Builder phone(&#64;Nullable final String phone) {{'{'}}
            this.phone = phone;
            return this;
        {{'}'}}

        public Builder street(final String street) {{'{'}}
            this.street = street;
            return this;
        {{'}'}}

        public Builder zip(final String zip) {{'{'}}
            this.zip = zip;
            return this;
        {{'}'}}

        public NativeCustomerDTO build() {{'{'}}
            return new NativeCustomerDTO(this);
        {{'}'}}
    {{'}'}}

    &#64;Nullable
    private Long id;
    private String firstName;
    private String lastName;
    private String street;
    private String zip;
    private String city;
    &#64;Nullable
    private String phone;
    &#64;Nullable
    private final String mail;

    NativeCustomerDTO() {{'{'}}
        this.id = null;
        this.firstName = null;
        this.lastName = null;
        this.street = null;
        this.zip = null;
        this.city = null;
        this.phone = null;
        this.mail = null;
    {{'}'}}

    NativeCustomerDTO(final Builder builder) {{'{'}}
        this.id = builder.id;
        this.firstName = checkNotNull(builder.firstName, "missing firstName in builder");
        this.lastName = checkNotNull(builder.lastName, "missing lastName in builder");
        this.street = checkNotNull(builder.street, "missing street in builder");
        this.zip = checkNotNull(builder.zip, "missing zip in builder");
        this.city = checkNotNull(builder.city, "missing city in builder");
        this.phone = builder.phone;
        this.mail = builder.mail;
    {{'}'}}

    public Optional&lt;Long&gt; getID() {{'{'}}
        return Optional.ofNullable(this.id);
    {{'}'}}

    public void setID(&#64;Nullable final Long id) {{'{'}}
        this.id = id;
    {{'}'}}

    public String getFirstName() {{'{'}}
        return this.firstName;
    {{'}'}}

    public void setFirstName(final String firstName) {{'{'}}
        this.firstName = firstName;
    {{'}'}}

    public String getLastName() {{'{'}}
        return this.lastName;
    {{'}'}}

    public void setLastName(final String lastName) {{'{'}}
        this.lastName = lastName;
    {{'}'}}

    public String getStreet() {{'{'}}
        return this.street;
    {{'}'}}

    public void setStreet(final String street) {{'{'}}
        this.street = street;
    {{'}'}}

    public String getZIP() {{'{'}}
        return this.zip;
    {{'}'}}

    public void setZIP(final String zip) {{'{'}}
        this.zip = zip;
    {{'}'}}

    public String getCity() {{'{'}}
        return this.city;
    {{'}'}}

    public void setCity(final String city) {{'{'}}
        this.city = city;
    {{'}'}}

    public Optional&lt;String&gt; getPhone() {{'{'}}
        return Optional.ofNullable(this.phone);
    {{'}'}}

    public void setPhone(final String phone) {{'{'}}
        this.phone = phone;
        {{'}'}}

    public Optional&lt;String&gt; getMail() {{'{'}}
        return Optional.ofNullable(this.mail);
    {{'}'}}

    &#64;Override
    public String toString() {{'{'}}
        //&#64;formatter:off
        return MoreObjects.toStringHelper(this)
                .add("id", this.id)
                .add("firstName", this.firstName)
                .add("lastName", this.lastName)
                .add("street", this.street)
                .add("zip", this.zip)
                .add("city", this.city)
                .add("phone", this.phone)
                .add("mail", this.mail)
                .toString();
        // &#64;formatter:on
    {{'}'}}

    &#64;Override
    public int hashCode() {{'{'}}
        return Objects.hash(this.id, this.firstName, this.lastName, this.street, this.zip, this.city, this.phone, this.mail);
    {{'}'}}

    &#64;Override
    public boolean equals(final Object other) {{'{'}}

        if (other == this) {{'{'}}
            return true;
        {{'}'}}
        if (!(other instanceof NativeCustomerDTO)) {{'{'}}
            return false;
        {{'}'}}
        final NativeCustomerDTO nativeCustomerDTO = (NativeCustomerDTO) other;

        return  Objects.equals(this.id, nativeCustomerDTO.id)
                && Objects.equals(this.firstName, nativeCustomerDTO.firstName)
                && Objects.equals(this.lastName, nativeCustomerDTO.lastName)
                && Objects.equals(this.street, nativeCustomerDTO.street)
                && Objects.equals(this.zip, nativeCustomerDTO.zip)
                && Objects.equals(this.city, nativeCustomerDTO.city)
                && Objects.equals(this.phone, nativeCustomerDTO.phone)
                && Objects.equals(this.mail, nativeCustomerDTO.mail);
    {{'}'}}
{{'}'}}
                    </code>
                </pre>
                </section>
                <section>
                    <h3 i18n>Vergleichstabelle</h3>
                    <table border="1" cellpadding="10">
                        <thead>
                            <tr class="tableHeader">
                                <th i18n>Kriterium</th>
                                <th i18n>Lombok</th>
                                <th i18n>Java Records</th>
                                <th i18n>Native Implementierung</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="row1">
                                <td i18n>Boilerplate-Code</td>
                                <td i18n>Reduziert erheblich</td>
                                <td i18n>Automatische Generierung, sehr reduziert</td>
                                <td i18n>Vielleicht manueller Code</td>
                            </tr>
                            <tr class="row0">
                                <td i18n>Flexibilität</td>
                                <td i18n>Mittel (durch Annotationen anpassbar)</td>
                                <td i18n>Eingeschränkt (festgelegte Struktur)</td>
                                <td i18n>Sehr hoch (volle Kontrolle)</td>
                            </tr>
                            <tr class="row1">
                                <td i18n>Verständlichkeit</td>
                                <td i18n>Kann versteckte Funktionalität enthalten</td>
                                <td i18n>Klar und einfach</td>
                                <td i18n>Direkt und transparent</td>
                            </tr>
                            <tr class="row0">
                                <td i18n>Tooling</td>
                                <td i18n>Benötigt Lombok-Plugin und -Konfiguration</td>
                                <td i18n>Teil des JDK ab Java 16, keine Extras nötig</td>
                                <td i18n>Keine zusätzlichen Tools erforderlich</td>
                            </tr>
                            <tr class="row1">
                                <td i18n>Lernkurve</td>
                                <td i18n>Erfordert Verständnis der Annotationen</td>
                                <td i18n>Einfach bei Verständnis von Unveränderlichkeit</td>
                                <td i18n>Keine speziellen Kenntnisse erforderlich</td>
                            </tr>
                            <tr class="row0">
                                <td i18n>Unveränderlichkeit</td>
                                <td i18n>Nicht standardmäßig</td>
                                <td i18n>Standardmäßig unveränderlich</td>
                                <td i18n>Abhängig von der Implementierung</td>
                            </tr>
                            <tr class="row1">
                                <td i18n>Anpassbarkeit</td>
                                <td i18n>Einige Anpassungen möglich</td>
                                <td i18n>Keine Anpassung von automatischen Methoden</td>
                                <td i18n>Vollständig anpassbar</td>
                            </tr>
                            <tr class="row0">
                                <td i18n>Thread-Sicherheit</td>
                                <td i18n>Abhängig von der Implementierung</td>
                                <td i18n>Inherently thread-sicher durch Unveränderlichkeit</td>
                                <td i18n>Abhängig von der Implementierung</td>
                            </tr>
                            <tr class="row1">
                                <td i18n>Zusätzliche Abhängigkeiten</td>
                                <td i18n>Benötigt Lombok-Bibliothek</td>
                                <td i18n>Keine zusätzlichen Abhängigkeiten</td>
                                <td i18n>Keine zusätzlichen Abhängigkeiten</td>
                            </tr>
                            <tr class="row0">
                                <td i18n>Kompatibilität</td>
                                <td i18n>Kann mit anderen Tools kollidieren</td>
                                <td i18n>Gute Kompatibilität in modernem Java</td>
                                <td i18n>Universell kompatibel</td>
                            </tr>
                            <tr class="row1">
                                <td i18n>Wartung</td>
                                <td i18n>Weniger Wartungsaufwand</td>
                                <td i18n>Minimaler Wartungsaufwand</td>
                                <td i18n>Höherer Wartungsaufwand bei Änderungen</td>
                            </tr>
                        </tbody>
                    </table>
                </section>
                <section>
                    <h3 i18n>Fazit</h3>
                    <p i18n>
                        Zusammenfassend lässt sich sagen, dass die Wahl zwischen Lombok, Java Records und der
                        nativen Implementierung von Ihren spezifischen Anforderungen und Vorlieben abhängt. Jeder
                        Ansatz hat seine Vor- und Nachteile, die berücksichtigt werden sollten.
                    </p>
                </section>

            </section>
        </div>
    </section>

</div>